/**
 * ComparisonClimbsGrid
 *
 * Two sections:
 *   1. Common Climbs (primary) — both runners' times measured over the exact
 *      same course segment (the overlap of their individually detected climb
 *      boundaries).  Computed by the backend via compute_common_climbs().
 *      Sorted by |Δ time| descending.
 *
 *   2. Detected Climbs (secondary, collapsible) — per-runner full climb data
 *      matched by DTW-aligned course position.  Provided as a reference for
 *      the full climb boundaries each runner's watch detected.
 *
 * Double-click a row → onClimbFocus(startM, endM) → parent zooms GapChart.
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Chip,
  useTheme,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

const CATEGORY_COLORS = {
  'R-C4': '#facc15',
  'R-C3': '#f97316',
  'R-C2': '#ef4444',
  'R-C1': '#dc2626',
  'R-HC': '#7f1d1d',
};

function formatDuration(s) {
  if (s == null) return '—';
  const m = Math.floor(Math.abs(s) / 60);
  const sec = Math.floor(Math.abs(s) % 60);
  const sign = s < 0 ? '−' : '';
  return `${sign}${m}:${sec.toString().padStart(2, '0')}`;
}

function formatPace(v) {
  if (v == null) return '—';
  const m = Math.floor(v);
  const s = Math.round((v - m) * 60);
  return `${m}:${s.toString().padStart(2, '0')} /km`;
}

function formatHr(v) {
  return v != null ? `${Math.round(v)} bpm` : '—';
}

function CategoryChip({ category, colors }) {
  const color = CATEGORY_COLORS[category] || colors.grey[400];
  return (
    <Chip
      label={category || '—'}
      size="small"
      sx={{ backgroundColor: color, color: '#fff', fontWeight: 'bold', fontSize: '0.65rem', height: 20 }}
    />
  );
}

function DeltaCell({ value, lowerIsBetter = true }) {
  if (value == null) return <TableCell align="center" sx={{ color: 'inherit' }}>—</TableCell>;
  // Colour shows WHO WON (blue = A, orange = B); the number is just the margin, with
  // no +/- sign — a "+10" looked like A was slower when it actually meant A won by 10.
  const bWon = lowerIsBetter ? value < -5 : value > 5;
  const aWon = lowerIsBetter ? value > 5 : value < -5;
  const color = aWon ? COLOR_A : bWon ? COLOR_B : undefined;
  return (
    <TableCell align="center" sx={{ color, fontWeight: 'bold' }}>
      {formatDuration(Math.abs(value))}
    </TableCell>
  );
}

// ---------------------------------------------------------------------------
// Section 1 — Common Climbs (primary, backend-computed)
// ---------------------------------------------------------------------------

function CommonClimbsTable({ commonClimbs, onClimbFocus, selectedClimb, onClimbSelect }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  // sortBy: 'start' (default) or 'delta'
  const [sortBy, setSortBy] = useState('start');
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    return [...commonClimbs].sort((a, b) => {
      let valA, valB;
      if (sortBy === 'delta') {
        valA = Math.abs(a.time_delta_s ?? 0);
        valB = Math.abs(b.time_delta_s ?? 0);
      } else {
        valA = a.start_distance_m ?? 0;
        valB = b.start_distance_m ?? 0;
      }
      return sortDir === 'asc' ? valA - valB : valB - valA;
    });
  }, [commonClimbs, sortBy, sortDir]);

  const handleSortBy = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'start' ? 'asc' : 'desc');
    }
  };

  return (
    <Box sx={{ overflowX: 'auto', mb: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 80 }}>{t('catHeader', 'Cat')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 70 }}>
              <TableSortLabel
                active={sortBy === 'start'}
                direction={sortBy === 'start' ? sortDir : 'asc'}
                onClick={() => handleSortBy('start')}
                sx={{ color: 'inherit !important' }}
              >
                {t('startKmHeader', 'Start km')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 70 }}>{t('lengthHeader', 'Length')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 60 }}>{t('gainHeader', 'Gain')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>{t('aTimeHeader', 'A Time')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>{t('bTimeHeader', 'B Time')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold' }}>
              <TableSortLabel
                active={sortBy === 'delta'}
                direction={sortBy === 'delta' ? sortDir : 'desc'}
                onClick={() => handleSortBy('delta')}
                sx={{ color: 'inherit !important' }}
              >
                {t('deltaTimeHeader', 'Δ Time')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>{t('aPaceHeader', 'A Pace')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>{t('bPaceHeader', 'B Pace')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>{t('aHrHeader', 'A HR')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>{t('bHrHeader', 'B HR')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((c, i) => {
            const startKm = c.start_distance_m != null ? `${(c.start_distance_m / 1000).toFixed(1)}` : '—';
            const lengthKm = c.length_m != null ? `${(c.length_m / 1000).toFixed(2)} km` : '—';
            const gain = c.elevation_gain_m != null ? `${Math.round(c.elevation_gain_m)} m` : '—';
            const isSelected =
              selectedClimb != null &&
              Math.abs((c.start_distance_m ?? -1) - selectedClimb.start_distance_m) < 1;
            return (
              <TableRow
                key={i}
                hover
                onClick={() => {
                  if (onClimbSelect && c.start_distance_m != null && c.end_distance_m != null) {
                    onClimbSelect({ start_distance_m: c.start_distance_m, end_distance_m: c.end_distance_m });
                  }
                }}
                onDoubleClick={() => {
                  if (onClimbFocus && c.start_distance_m != null && c.end_distance_m != null) {
                    onClimbFocus(c.start_distance_m, c.end_distance_m);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: isSelected ? `${colors.blueAccent[900]} !important` : undefined,
                  outline: isSelected ? `2px solid ${colors.blueAccent[500]}` : undefined,
                  outlineOffset: '-2px',
                  '&:nth-of-type(even)': { backgroundColor: isSelected ? undefined : colors.grey[800] },
                  '&:hover': { backgroundColor: isSelected ? `${colors.blueAccent[800]} !important` : colors.grey[700] },
                }}
              >
                <TableCell><CategoryChip category={c.category} colors={colors} /></TableCell>
                <TableCell sx={{ color: colors.grey[300] }}>km {startKm}</TableCell>
                <TableCell sx={{ color: colors.grey[400], fontSize: '0.75rem' }}>{lengthKm}</TableCell>
                <TableCell sx={{ color: colors.grey[400], fontSize: '0.75rem' }}>{gain}</TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>{formatDuration(c.a?.duration_s)}</TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>{formatDuration(c.b?.duration_s)}</TableCell>
                <DeltaCell value={c.time_delta_s} lowerIsBetter={true} />
                <TableCell sx={{ color: COLOR_A, fontSize: '0.75rem' }}>{formatPace(c.a?.avg_pace)}</TableCell>
                <TableCell sx={{ color: COLOR_B, fontSize: '0.75rem' }}>{formatPace(c.b?.avg_pace)}</TableCell>
                <TableCell sx={{ color: COLOR_A, fontSize: '0.75rem' }}>{formatHr(c.a?.avg_hr)}</TableCell>
                <TableCell sx={{ color: COLOR_B, fontSize: '0.75rem' }}>{formatHr(c.b?.avg_hr)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — Detected Climbs (reference, frontend-matched)
// ---------------------------------------------------------------------------

function matchClimbs(climbsA, climbsB, MATCH_THRESHOLD_M = 150) {
  if (!climbsA?.length && !climbsB?.length) return [];
  const used = new Set();
  const pairs = [];
  const climbsAArr = climbsA || [];
  const climbsBArr = climbsB || [];

  climbsAArr.forEach((ca) => {
    let bestB = null;
    let bestDiff = Infinity;
    climbsBArr.forEach((cb, j) => {
      if (used.has(j)) return;
      const diff = Math.abs(ca.start_distance_m - cb.start_distance_m);
      if (diff < MATCH_THRESHOLD_M && diff < bestDiff) {
        bestDiff = diff;
        bestB = { climb: cb, idx: j };
      }
    });
    if (bestB) {
      used.add(bestB.idx);
      pairs.push({ climbA: ca, climbB: bestB.climb });
    } else {
      pairs.push({ climbA: ca, climbB: null });
    }
  });
  climbsBArr.forEach((cb, j) => {
    if (!used.has(j)) pairs.push({ climbA: null, climbB: cb });
  });
  return pairs;
}

function DetectedClimbsTable({ climbsA, climbsB, onClimbFocus }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState('start');
  const [sortDir, setSortDir] = useState('asc');

  const pairs = useMemo(() => matchClimbs(climbsA, climbsB), [climbsA, climbsB]);

  const sorted = useMemo(() => {
    return [...pairs].sort((a, b) => {
      let valA, valB;
      if (sortBy === 'delta') {
        const da = a.climbA?.duration_s ?? 0;
        const db = a.climbB?.duration_s ?? 0;
        const ea = b.climbA?.duration_s ?? 0;
        const eb = b.climbB?.duration_s ?? 0;
        valA = Math.abs(da - db);
        valB = Math.abs(ea - eb);
      } else {
        const refA = a.climbA || a.climbB;
        const refB = b.climbA || b.climbB;
        valA = refA?.start_distance_m ?? 0;
        valB = refB?.start_distance_m ?? 0;
      }
      return sortDir === 'asc' ? valA - valB : valB - valA;
    });
  }, [pairs, sortBy, sortDir]);

  const handleSortBy = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'start' ? 'asc' : 'desc');
    }
  };

  if (!pairs.length) return null;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 80 }}>{t('catHeader', 'Cat')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold', minWidth: 70 }}>
              <TableSortLabel
                active={sortBy === 'start'}
                direction={sortBy === 'start' ? sortDir : 'asc'}
                onClick={() => handleSortBy('start')}
                sx={{ color: 'inherit !important' }}
              >
                {t('startKmHeader', 'Start km')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>{t('aDurationHeader', 'A Duration')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>{t('bDurationHeader', 'B Duration')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[200], fontWeight: 'bold' }}>
              <TableSortLabel
                active={sortBy === 'delta'}
                direction={sortBy === 'delta' ? sortDir : 'desc'}
                onClick={() => handleSortBy('delta')}
                sx={{ color: 'inherit !important' }}
              >
                {t('deltaTimeHeader', 'Δ Time')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>{t('aPaceHeader', 'A Pace')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>{t('bPaceHeader', 'B Pace')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>{t('aHrHeader', 'A HR')}</TableCell>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>{t('bHrHeader', 'B HR')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((pair, i) => {
            const ca = pair.climbA;
            const cb = pair.climbB;
            const refClimb = ca || cb;
            const timeDelta = ca && cb ? (cb.duration_s ?? 0) - (ca.duration_s ?? 0) : null;
            const startKm = refClimb?.start_distance_m != null
              ? `${(refClimb.start_distance_m / 1000).toFixed(1)}` : '—';
            return (
              <TableRow
                key={i}
                hover
                onDoubleClick={() => {
                  if (refClimb && onClimbFocus) {
                    onClimbFocus(refClimb.start_distance_m, refClimb.end_distance_m);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  '&:nth-of-type(even)': { backgroundColor: colors.grey[800] },
                  '&:hover': { backgroundColor: colors.grey[700] },
                }}
              >
                <TableCell><CategoryChip category={refClimb?.category} colors={colors} /></TableCell>
                <TableCell sx={{ color: colors.grey[300] }}>km {startKm}</TableCell>
                <TableCell sx={{ color: ca ? colors.grey[100] : colors.grey[600] }}>
                  {ca ? formatDuration(ca.duration_s) : '—'}
                </TableCell>
                <TableCell sx={{ color: cb ? colors.grey[100] : colors.grey[600] }}>
                  {cb ? formatDuration(cb.duration_s) : '—'}
                </TableCell>
                <DeltaCell value={timeDelta} lowerIsBetter={true} />
                <TableCell sx={{ color: COLOR_A, fontSize: '0.75rem' }}>
                  {ca ? formatPace(ca.avg_pace) : '—'}
                </TableCell>
                <TableCell sx={{ color: COLOR_B, fontSize: '0.75rem' }}>
                  {cb ? formatPace(cb.avg_pace) : '—'}
                </TableCell>
                <TableCell sx={{ color: COLOR_A, fontSize: '0.75rem' }}>
                  {ca ? formatHr(ca.avg_heart_rate) : '—'}
                </TableCell>
                <TableCell sx={{ color: COLOR_B, fontSize: '0.75rem' }}>
                  {cb ? formatHr(cb.avg_heart_rate) : '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ComparisonClimbsGrid({
  climbsA = [],
  climbsB = [],
  commonClimbs = null,   // from alignment.common_climbs (backend-computed)
  onClimbFocus,          // double-click: zoom Race Dynamics + switch tab
  selectedClimb = null,  // { start_distance_m, end_distance_m } — highlight
  onClimbSelect = null,  // single-click: update selectedClimb in parent
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const [detectedOpen, setDetectedOpen] = useState(false);

  const hasCommon = commonClimbs && commonClimbs.length > 0;
  const hasAny = climbsA.length > 0 || climbsB.length > 0;

  if (!hasAny && !hasCommon) {
    return (
      <Box p={3} textAlign="center">
        <Typography color={colors.grey[500]}>{t('noClimbsDetected', 'No climbs detected in either workout')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ---- Common Climbs (primary) ---- */}
      {hasCommon ? (
        <>
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
            {t('climbComparison', 'Climb Comparison')}
          </Typography>
          <Typography variant="caption" color={colors.grey[400]} display="block" mb={1}>
            {t('bothRunnersMeasured', 'Both runners measured over the same course segment (smallest common overlap). Double-click a row to zoom Race Dynamics chart to that climb.')}
          </Typography>
          <CommonClimbsTable
            commonClimbs={commonClimbs}
            onClimbFocus={onClimbFocus}
            selectedClimb={selectedClimb}
            onClimbSelect={onClimbSelect}
          />
        </>
      ) : (
        <>
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
            {t('climbComparison', 'Climb Comparison')}
          </Typography>
          <Typography variant="caption" color={colors.grey[400]} display="block" mb={2}>
            {t('doubleClickToZoom', 'Double-click a row to zoom Race Dynamics chart to that climb.')}
          </Typography>
          <DetectedClimbsTable climbsA={climbsA} climbsB={climbsB} onClimbFocus={onClimbFocus} />
        </>
      )}

      {/* ---- Detected Climbs (secondary / reference) ---- */}
      {hasCommon && hasAny && (
        <Box mt={2}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setDetectedOpen((v) => !v)}
          >
            <Typography variant="subtitle2" color={colors.grey[400]}>
              {t('fullClimbBoundaries', 'Full Climb Boundaries (reference)')}
            </Typography>
            <IconButton size="small" sx={{ color: colors.grey[500], p: 0 }}>
              {detectedOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={detectedOpen}>
            <Typography variant="caption" color={colors.grey[500]} display="block" mb={1} mt={0.5}>
              {t('perRunnerBoundaries', "Each runner's individually detected climb boundaries — may differ slightly in start/end km.")}
            </Typography>
            <DetectedClimbsTable climbsA={climbsA} climbsB={climbsB} onClimbFocus={onClimbFocus} />
          </Collapse>
        </Box>
      )}
    </Box>
  );
}
